import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useMutation } from "convex/react";
import {
  Bell,
  Check,
  CheckCircle2,
  LockOpen,
  Rocket,
  X,
  type LucideIcon,
} from "lucide-react";
import { useReducedMotion } from "motion/react";

import { useScreen0Language } from "@/features/language";
import { getScreen0IntlLocale } from "@/features/language/language-registry";
import { formatTemplate, getTranslations } from "@/i18n";
import { privacyUrl, termsUrl } from "@/lib/env";
import { mutationRef } from "@/lib/convex/refs";
import { useDeviceId } from "@/lib/device/DeviceProvider";
import { computeTrialReminderNotifyAt } from "@/lib/notifications/trialReminder";
import { markPremiumExplicitlyUnlocked } from "@/lib/purchases/premiumUnlockStorage";
import {
  buildPaywallPricing,
  type PaywallPricing,
} from "@/lib/purchases/paywallPricing";
import { getRestoreNothingFoundMessage } from "@/lib/purchases/restoreMessaging";
import {
  configurePurchases,
  getConfigureFailureReason,
  getPaywallCatalog,
  isPurchasesSupported,
  purchaseSelectedPlan,
  restorePurchases,
  type PaywallCatalog,
  type PlanId,
} from "@/lib/purchases/revenuecat";

import { EmphasisText } from "../EmphasisText";
import { LegalRouteLink } from "@/pages/legal/LegalRouteLink";
import { useOptionalOnboardingAnalytics } from "@/lib/onboarding/OnboardingAnalyticsContext";
import { useOptionalTabPaywallAnalytics } from "@/lib/monetization/TabPaywallAnalyticsContext";

type RegisterTrialReminderArgs = {
  anonymousDeviceId: string;
  trialStartedAt: number;
  trialEndsAt: number;
  notifyAt: number;
  timezone: string;
  languageCode: "it" | "en" | "fr" | "es" | "de";
};

const registerTrialReminderRef = mutationRef<
  RegisterTrialReminderArgs,
  { registered: boolean }
>("trialReminders:register");

const TIMELINE_ICONS: LucideIcon[] = [CheckCircle2, LockOpen, Bell, Rocket];

function catalogAnalyticsMetadata(catalog: PaywallCatalog) {
  return {
    yearly_price: String(catalog.yearly.product.price),
    yearly_currencyCode: catalog.yearly.product.currencyCode,
    yearly_priceString: catalog.yearly.product.priceString,
    yearly_productIdentifier: catalog.yearly.product.identifier,
    lifetime_price: String(catalog.lifetime.product.price),
    lifetime_currencyCode: catalog.lifetime.product.currencyCode,
    lifetime_priceString: catalog.lifetime.product.priceString,
    lifetime_productIdentifier: catalog.lifetime.product.identifier,
  };
}

function planProduct(catalog: PaywallCatalog, plan: PlanId) {
  return plan === "yearly" ? catalog.yearly.product : catalog.lifetime.product;
}

type PaywallSlideProps = {
  onAdvance: () => void;
  /** Tab-gate only: scrollable close control; omitted in onboarding paywall. */
  onDismiss?: () => void;
  analyticsStep?: number;
};

export function PaywallSlide({ onAdvance, onDismiss, analyticsStep = 3 }: PaywallSlideProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const p = t.onboarding.paywall;
  const intlLocale = getScreen0IntlLocale(languageCode);
  const prefersReducedMotion = useReducedMotion();
  const deviceId = useDeviceId();
  const registerTrialReminder = useMutation(registerTrialReminderRef);
  const analytics = useOptionalTabPaywallAnalytics() ?? useOptionalOnboardingAnalytics();
  const [selectedPlan, setSelectedPlan] = useState<PlanId>("yearly");
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreNotice, setRestoreNotice] = useState<{ text: string; isError: boolean } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [pricing, setPricing] = useState<PaywallPricing | null>(null);
  const catalogRef = useRef<PaywallCatalog | null>(null);
  const [pricingLoading, setPricingLoading] = useState(isPurchasesSupported());
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  const loadPricing = useCallback(async () => {
    if (!isPurchasesSupported()) {
      setPricingLoading(false);
      return;
    }

    setPricingLoading(true);
    setError(null);

    const configured = await configurePurchases(deviceId);
    if (!configured) {
      catalogRef.current = null;
      setPricing(null);
      setPricingLoading(false);
      const reason = getConfigureFailureReason();
      setError(reason ? `${p.purchase.configError} (${reason})` : p.purchase.configError);
      return;
    }

    try {
      const catalog = await getPaywallCatalog();
      if (!catalog) {
        catalogRef.current = null;
        console.warn("Paywall: RevenueCat offering or products unavailable");
        setPricing(null);
        setError(p.purchase.offeringsError);
        return;
      }

      catalogRef.current = catalog;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      analytics?.trackPaywallAction("catalog_loaded", {
        intlLocale,
        timezone,
        metadata: {
          ...catalogAnalyticsMetadata(catalog),
          platform: Capacitor.getPlatform(),
        },
      });

      setPricing(
        buildPaywallPricing(catalog.yearly.product, catalog.lifetime.product, intlLocale, {
          yearlyBadgeWithTrial: p.yearlyBadgeWithTrial,
          yearlyBadgeNoTrial: p.yearlyBadgeNoTrial,
        }),
      );
    } catch (loadError) {
      catalogRef.current = null;
      console.error("Paywall: failed to load store pricing", loadError);
      setPricing(null);
      setError(p.purchase.offeringsError);
    } finally {
      setPricingLoading(false);
    }
  }, [analytics, deviceId, intlLocale, p.purchase.configError, p.purchase.offeringsError, p.yearlyBadgeNoTrial, p.yearlyBadgeWithTrial]);

  useEffect(() => {
    void loadPricing();
  }, [loadPricing]);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const updateFooterHeight = () => {
      setFooterHeight(footer.offsetHeight);
    };

    updateFooterHeight();

    const observer = new ResizeObserver(updateFooterHeight);
    observer.observe(footer);
    return () => observer.disconnect();
  }, [error, pricingLoading, restoreNotice]);

  const isYearly = selectedPlan === "yearly";
  const ctaLabel = busy ? p.purchase.processing : isYearly ? p.ctaYearly : p.ctaLifetime;
  const trialDays = pricing?.yearly.trialDays ?? null;
  // Only block the CTA while pricing is actively loading. Once loading ends —
  // success OR failure — the button is clickable: the purchase path re-fetches
  // packages and surfaces a real, actionable error if the store is unavailable.
  const ctaDisabled = busy || (isPurchasesSupported() && pricingLoading);

  // `||` (not `??`): treat an empty priceString from a partially-hydrated package
  // the same as a missing price so we never render a blank or stuck "…".
  const yearlyPrice = pricing?.yearly.price || (pricingLoading ? "…" : "—");
  const yearlyBadge = pricing?.yearly.badge ?? undefined;
  const yearlySubtext =
    pricing?.yearly.monthlyPrice != null
      ? formatTemplate(p.yearlySubtext, { monthlyPrice: pricing.yearly.monthlyPrice })
      : "";
  const lifetimePrice = pricing?.lifetime.price || (pricingLoading ? "…" : "—");
  const lifetimeOriginalPrice = pricing?.lifetime.originalPrice ?? undefined;

  const handleCta = useCallback(async () => {
    if (busy || ctaDisabled) return;

    analytics?.trackCtaClick(analyticsStep, "primary");

    // Development (any platform, including web): skip the store and advance.
    if (import.meta.env.DEV) {
      analytics?.trackPaywallAction("trial_started_dev_skip", {
        paywallPlan: selectedPlan,
        metadata: { dev_skip: "true" },
      });
      onAdvance();
      return;
    }

    // Production web (or native without an SDK key): purchases are native-only.
    if (!isPurchasesSupported()) {
      setError(Capacitor.isNativePlatform() ? p.purchase.configError : p.purchase.webUnavailable);
      return;
    }

    setError(null);
    setRestoreNotice(null);
    setBusy(true);
    const outcome = await purchaseSelectedPlan(selectedPlan, deviceId);
    setBusy(false);

    switch (outcome.status) {
      case "success": {
        const catalog = catalogRef.current;
        const product = catalog ? planProduct(catalog, selectedPlan) : null;
        const isTrial = selectedPlan === "yearly" && outcome.trial != null;
        const revenueStatus = isTrial ? "pending_trial" : "realized";
        const paywallAction = isTrial
          ? "trial_started"
          : selectedPlan === "lifetime"
            ? "lifetime_activated"
            : "purchase_success";

        analytics?.trackPaywallAction(paywallAction, {
          paywallPlan: selectedPlan,
          revenueStatus,
          price: product?.price,
          currencyCode: product?.currencyCode,
          priceString: product?.priceString,
          productIdentifier: product?.identifier,
          trialEndsAtMs: outcome.trial?.trialEndsAtMs,
          intlLocale,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metadata: { platform: Capacitor.getPlatform() },
        });
        // Yearly free-trial reminder (Day-5 / trialEnd-2days push): best-effort,
        // never blocks onboarding — a failure here shouldn't trap the user.
        if (selectedPlan === "yearly" && outcome.trial) {
          const trialEndsAt = outcome.trial.trialEndsAtMs;
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          registerTrialReminder({
            anonymousDeviceId: deviceId,
            trialStartedAt: Date.now(),
            trialEndsAt,
            notifyAt: computeTrialReminderNotifyAt(trialEndsAt, timezone),
            timezone,
            languageCode,
          }).catch((registerError) => {
            console.warn("Trial reminder registration failed", registerError);
          });
        }
        onAdvance();
        return;
      }
      case "cancelled":
        analytics?.trackPaywallAction("purchase_cancelled", {
          paywallPlan: selectedPlan,
          metadata: { platform: Capacitor.getPlatform() },
        });
        return;
      case "not_entitled":
        analytics?.trackPaywallAction("purchase_not_entitled", {
          paywallPlan: selectedPlan,
          metadata: { platform: Capacitor.getPlatform() },
        });
        setError(p.purchase.notEntitled);
        return;
      case "unsupported":
        analytics?.trackPaywallAction("purchase_unsupported", {
          paywallPlan: selectedPlan,
          metadata: { platform: Capacitor.getPlatform() },
        });
        setError(p.purchase.webUnavailable);
        return;
      case "error":
        console.warn("Paywall purchase failed:", outcome.reason);
        analytics?.trackPaywallAction("purchase_error", {
          paywallPlan: selectedPlan,
          metadata: { reason: outcome.reason, platform: Capacitor.getPlatform() },
        });
        if (outcome.reason === "not_configured") {
          const reason = getConfigureFailureReason();
          setError(reason ? `${p.purchase.configError} (${reason})` : p.purchase.configError);
          return;
        }
        if (outcome.reason === "no_offering" || outcome.reason === "package_not_found") {
          setError(p.purchase.offeringsError);
          return;
        }
        setError(p.purchase.genericError);
        return;
    }
  }, [analytics, analyticsStep, busy, ctaDisabled, deviceId, intlLocale, languageCode, onAdvance, p.purchase, registerTrialReminder, selectedPlan]);

  const handleRestore = useCallback(async () => {
    if (busy || restoring) return;
    analytics?.trackCtaClick(analyticsStep, "secondary");
    setError(null);
    setRestoreNotice(null);

    // Development (any platform, including web): don't hit the store.
    if (import.meta.env.DEV) {
      console.info("[paywall] restore skipped in DEV");
      setRestoreNotice({
        text: getRestoreNothingFoundMessage(p.purchase),
        isError: false,
      });
      return;
    }

    // Production web (or native without an SDK key): purchases are native-only.
    if (!isPurchasesSupported()) {
      setRestoreNotice({
        text: Capacitor.isNativePlatform() ? p.purchase.configError : p.purchase.webUnavailable,
        isError: true,
      });
      return;
    }

    setRestoring(true);
    const outcome = await restorePurchases(deviceId);
    setRestoring(false);

    switch (outcome.status) {
      case "restored":
        analytics?.trackPaywallAction("restore_succeeded");
        await markPremiumExplicitlyUnlocked();
        onAdvance();
        return;
      case "nothing_to_restore":
        analytics?.trackPaywallAction("restore_nothing_found");
        setRestoreNotice({
          text: getRestoreNothingFoundMessage(p.purchase),
          isError: false,
        });
        return;
      case "unsupported":
        analytics?.trackPaywallAction("restore_unsupported");
        setRestoreNotice({ text: p.purchase.webUnavailable, isError: true });
        return;
      case "error":
        console.warn("Paywall restore failed:", outcome.reason);
        analytics?.trackPaywallAction("restore_error", {
          metadata: { reason: outcome.reason, platform: Capacitor.getPlatform() },
        });
        setRestoreNotice({ text: p.purchase.restoreError, isError: true });
        return;
    }
  }, [analytics, analyticsStep, busy, deviceId, onAdvance, p.purchase, restoring]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        className="relative z-[1] min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-[max(18px,env(safe-area-inset-left))] pr-[max(18px,env(safe-area-inset-right))]"
        style={{ paddingBottom: footerHeight > 0 ? footerHeight + 16 : undefined }}
      >
        <div className="relative w-full pt-2">
          {onDismiss ? (
            <button
              type="button"
              className="icon-button absolute right-0 top-2 z-[1] shrink-0"
              aria-label={t.common.close}
              onClick={onDismiss}
            >
              <X size={20} />
            </button>
          ) : null}

          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-0.5 opacity-40">
              <img
                src="/brand/screen0-app-icon.png"
                alt=""
                width={28}
                height={28}
                className="h-7 w-7 shrink-0 rounded-[7px]"
                decoding="async"
              />
              <span className="text-base font-medium tracking-tight text-[oklch(90%_0.02_150)]">
                {t.common.appName}
              </span>
            </div>

            <h1 className="mt-5 max-w-[22rem] font-serif text-[1.65rem] font-semibold leading-tight text-[oklch(94%_0.02_150)] sm:max-w-[26rem] sm:text-[1.85rem]">
              <EmphasisText text={p.headline} />
            </h1>
          </div>
        </div>

        <ol className="relative mt-8 flex flex-col gap-6 pb-4">
          <div
            className="pointer-events-none absolute bottom-4 left-[1.125rem] top-4 w-px bg-gradient-to-b from-[oklch(72%_0.14_75)] via-[oklch(58%_0.1_70)] to-[oklch(32%_0.04_160)]"
            aria-hidden
          />

          {p.timeline.map((row, index) => {
            const Icon = TIMELINE_ICONS[index];
            const isActive = index < 2;
            const description =
              index === 1 && trialDays
                ? formatTemplate(p.timelineTrialDescriptionWithTrial, { trialDays })
                : index === 1
                  ? p.timelineTrialDescriptionNoTrial
                  : row.description;

            return (
              <li key={row.title} className="relative flex gap-3.5 pl-0">
                <span
                  className={`relative z-[1] flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isActive
                      ? "bg-[oklch(68%_0.12_75)] text-[oklch(18%_0.03_70)]"
                      : "bg-[oklch(38%_0.06_70)] text-[oklch(78%_0.08_85)]"
                  }`}
                  aria-hidden
                >
                  <Icon size={18} strokeWidth={2} />
                </span>

                <div className="min-w-0 flex-1 pt-0.5 text-left">
                  <p className="font-semibold leading-snug text-[oklch(92%_0.02_150)]">
                    {row.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-[oklch(72%_0.025_155)]">
                    <EmphasisText text={description} />
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div
        ref={footerRef}
        className="absolute inset-x-0 bottom-0 z-[2] rounded-t-3xl bg-[oklch(9%_0.025_160)] shadow-[inset_0_1px_0_oklch(100%_0_0_/_0.06)]"
      >
        <div className="flex flex-col gap-2.5 px-[max(18px,env(safe-area-inset-left))] pr-[max(18px,env(safe-area-inset-right))] pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
          <PaywallPlanCard
            selected={isYearly}
            onSelect={() => setSelectedPlan("yearly")}
            badge={yearlyBadge}
            title={p.yearlyTitle}
            subtext={yearlySubtext}
            price={yearlyPrice}
            loading={pricingLoading && isPurchasesSupported()}
          />

          <PaywallPlanCard
            selected={!isYearly}
            onSelect={() => setSelectedPlan("lifetime")}
            title={p.lifetimeTitle}
            subtext={p.lifetimeSubtext}
            price={lifetimePrice}
            originalPrice={lifetimeOriginalPrice}
            loading={pricingLoading && isPurchasesSupported()}
          />

          {error ? (
            <p
              className="text-center text-xs leading-relaxed text-[oklch(78%_0.09_35)]"
              role="alert"
            >
              {error}{" "}
              {error.startsWith(p.purchase.offeringsError) || error.startsWith(p.purchase.configError) ? (
                <button
                  type="button"
                  className="underline underline-offset-2"
                  onClick={() => void loadPricing()}
                >
                  {p.purchase.retry}
                </button>
              ) : null}
            </p>
          ) : null}

          <button
            type="button"
            className={`primary-action w-full uppercase tracking-[0.08em] ${
              prefersReducedMotion ? "" : "transition-transform"
            }`}
            onClick={() => void handleCta()}
            disabled={ctaDisabled}
            aria-busy={busy || pricingLoading}
          >
            {ctaLabel}
          </button>

          <button
            type="button"
            className="mx-auto min-h-9 px-3 text-xs font-medium text-[oklch(74%_0.025_155)] underline underline-offset-2 hover:text-[oklch(88%_0.02_150)] disabled:opacity-60"
            onClick={() => void handleRestore()}
            disabled={busy || restoring}
            aria-busy={restoring}
          >
            {restoring ? p.purchase.restoreProcessing : p.purchase.restorePurchases}
          </button>

          {restoreNotice ? (
            <p
              className={`text-center text-xs leading-relaxed ${
                restoreNotice.isError
                  ? "text-[oklch(78%_0.09_35)]"
                  : "text-[oklch(72%_0.025_155)]"
              }`}
              role={restoreNotice.isError ? "alert" : "status"}
            >
              {restoreNotice.text}
            </p>
          ) : null}

          <p className="text-center text-[0.6875rem] leading-relaxed text-[oklch(58%_0.02_155)]">
            {isYearly ? p.footerRenewal : p.footerLifetime}{" "}
            <LegalRouteLink href={termsUrl} className="underline underline-offset-2 hover:text-[oklch(72%_0.025_155)]">
              {p.termsLink}
            </LegalRouteLink>
            {" · "}
            <LegalRouteLink href={privacyUrl} className="underline underline-offset-2 hover:text-[oklch(72%_0.025_155)]">
              {p.privacyLink}
            </LegalRouteLink>
          </p>
        </div>
      </div>
    </div>
  );
}

type PaywallPlanCardProps = {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtext: string;
  price: string;
  originalPrice?: string;
  badge?: string;
  loading?: boolean;
};

function PaywallPlanCard({
  selected,
  onSelect,
  title,
  subtext,
  price,
  originalPrice,
  badge,
  loading = false,
}: PaywallPlanCardProps) {
  return (
    <button
      type="button"
      className={`relative w-full rounded-xl px-3.5 text-left transition-[box-shadow,background-color] ${
        badge ? "pb-3 pt-4" : "py-3"
      } ${
        selected
          ? "bg-[oklch(16%_0.03_160)] shadow-[inset_0_0_0_1.5px_oklch(72%_0.14_75)]"
          : "bg-[oklch(14%_0.025_160)] shadow-[inset_0_0_0_1px_oklch(100%_0_0_/_0.08)]"
      }`}
      onClick={onSelect}
      aria-pressed={selected}
      aria-busy={loading}
    >
      {badge ? (
        <span className="absolute -top-2.5 right-3 rounded-full bg-[oklch(68%_0.12_75)] px-2.5 py-0.5 text-[0.625rem] font-semibold leading-tight text-[oklch(16%_0.03_70)]">
          {badge}
        </span>
      ) : null}

      <div className="flex items-center gap-3">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
            selected
              ? "bg-[oklch(68%_0.12_75)] text-[oklch(16%_0.03_70)]"
              : "bg-[oklch(22%_0.03_160)] text-transparent"
          }`}
          aria-hidden
        >
          {selected ? <Check size={12} strokeWidth={3} /> : null}
        </span>

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[oklch(92%_0.02_150)]">{title}</p>
          <p className="mt-0.5 text-xs leading-snug text-[oklch(64%_0.025_155)]">{subtext}</p>
        </div>

        <div className="shrink-0 text-right">
          {originalPrice ? (
            <p className="text-xs text-[oklch(52%_0.02_155)] line-through">{originalPrice}</p>
          ) : null}
          <p
            className={`font-semibold text-[oklch(90%_0.02_150)] ${
              loading ? "animate-pulse opacity-60" : ""
            }`}
          >
            {price}
          </p>
        </div>
      </div>
    </button>
  );
}
